// The CMS Alignment Packet's condition model — spec docs/specs/2026-07-11-icd10-robustness.md.
//
// Single source of truth: the shared resolver (cross-root import — the same module
// WF1/WF15 inline via sync-icd10-maps, so app and workflows literally share one table).
// The packet is derived FRESH from the modal's live condition list + the plan's
// chosen subtrack on every render — NEVER read from the stored
// context.access_subtrack bundle (the stored bundle is exactly what goes stale).
//
// STRICT posture: only vetted {icd10, display} pairs can enter the payload. An
// entry the vocabulary can't resolve is rejected at input (validateConditionInput)
// or, when seeded from a legacy plan, surfaces as a BLOCKING unresolved row —
// there is no send-as-is escape hatch.

import {
  deriveIcdBundle,
  normalizeCondition,
  CONDITION_ICD10,
  CONDITION_LABEL,
  EXTENDED_ICD10,
  EXTENDED_NORMALIZE,
} from '../../../scripts/access-subtrack-resolver.mjs';

// Re-export for UI pickers (components import from logic/, never from ../scripts).
export { normalizeCondition };

type Json = Record<string, unknown>;

export interface ResolvedCondition {
  raw: string; // the modal's stored value (canonical label / extended display)
  label: string; // chip text
  icd10: string | null;
  display: string | null;
  extended: boolean; // CMS-only condition (never in the care-plan vocabulary)
  resolved: boolean;
}

// Strictly object pairs — the raw-string arm died with the strict posture (R9).
export type AlignmentConditionPayload = { icd10: string; display: string };

export interface AlignmentPacket {
  perCondition: ResolvedCondition[]; // chip view (label · code), one per entry
  cmsView: Array<{ icd10: string; display: string; extended: boolean }>; // what CMS receives (combination codes)
  payload: AlignmentConditionPayload[]; // fireAlignment / FHIR-preview input
  unresolvedCount: number; // > 0 ⇒ submit must be blocked
  subtrackId: string | null;
}

// Operator-typed raw ICD-10-CM code shape (e.g. "N18.3", "E11.9").
const ICD10_CODE = /^[A-Z]\d[A-Z0-9]?(?:\.[A-Z0-9]{1,4})?$/i;

function readContext(carePlan: Json | null | undefined): Json | null {
  let ctx: unknown = carePlan?.context ?? null;
  if (typeof ctx === 'string') { try { ctx = JSON.parse(ctx); } catch { ctx = null; } }
  return ctx && typeof ctx === 'object' ? (ctx as Json) : null;
}

export function readSubtrackId(carePlan: Json | null | undefined): string | null {
  const sub = readContext(carePlan)?.access_subtrack as Json | undefined;
  const id = sub?.subtrack_id;
  return id != null && id !== '' ? String(id) : null;
}

function coreTokenForCode(code: string): string | null {
  const found = Object.keys(CONDITION_ICD10).find((t) => CONDITION_ICD10[t]?.code === code);
  return found ?? null;
}
function extendedTokenFor(value: string): string | null {
  const k = value.trim().toLowerCase();
  const alias = EXTENDED_NORMALIZE[k];
  if (alias) return alias;
  const code = value.trim().toUpperCase();
  const found = Object.keys(EXTENDED_ICD10).find((t) => EXTENDED_ICD10[t]?.code === code);
  return found ?? null;
}

// Duplicate check by resolved identity, not raw string ('HTN' vs 'Hypertension').
export function sameCondition(a: string, b: string): boolean {
  const idOf = (v: string) => normalizeCondition(v) ?? extendedTokenFor(v) ?? v.trim().toLowerCase();
  return idOf(a) === idOf(b);
}

/**
 * STRICT input gate for the "+ Add condition" editor. Accepts a known
 * label/alias (core or extended) or a code that reverse-maps to either table;
 * returns the canonical stored value, or an error message for inline display.
 */
export function validateConditionInput(
  input: string,
  existing: string[],
): { ok: true; value: string } | { ok: false; error: string } {
  const value = String(input ?? '').trim();
  if (!value) return { ok: false, error: 'Enter a condition or an ICD-10 code.' };

  let stored: string | null = null;
  const coreTok = normalizeCondition(value)
    ?? (ICD10_CODE.test(value) ? coreTokenForCode(value.toUpperCase()) : null);
  if (coreTok) stored = CONDITION_LABEL[coreTok] ?? null;
  if (!stored) {
    const extTok = extendedTokenFor(value);
    stored = (extTok && EXTENDED_ICD10[extTok]?.display) || null;
  }
  if (!stored) {
    return {
      ok: false,
      error: `"${value}" is not a supported condition or ICD-10 code — pick from the list, or ask to add it to the ACCESS vocabulary.`,
    };
  }
  if (existing.some((e) => sameCondition(e, stored as string))) {
    return { ok: false, error: `${stored} is already on the packet.` };
  }
  return { ok: true, value: stored };
}

// Known-vocabulary suggestions for the editor datalist (core labels first).
export function conditionSuggestions(): string[] {
  return [
    ...Object.values(CONDITION_LABEL),
    ...Object.values(EXTENDED_ICD10).map((e) => e.display),
  ];
}

/**
 * Build the packet from the modal's LIVE condition list — derive-fresh, never
 * the stored bundle. Core entries feed deriveIcdBundle (which honors the plan's
 * chosen subtrack → combination codes like E11.22); extended entries ride the
 * cms view flagged; anything unresolvable is a BLOCKING row.
 */
export function buildAlignmentPacket(
  conditions: string[] | null | undefined,
  carePlan: Json | null | undefined,
): AlignmentPacket {
  const entries = (Array.isArray(conditions) ? conditions : []).map((c) => String(c));
  const subtrackId = readSubtrackId(carePlan);
  // Canonicalize BEFORE deriving so a seeded raw code ('E11.9') resolves the same
  // way the chips do — otherwise the bundle drops it silently (green chip, absent
  // from the wire: the exact class the strict posture exists to kill).
  const canonicalEntries = entries.map((raw) => {
    const coreTok = normalizeCondition(raw)
      ?? (ICD10_CODE.test(raw.trim()) ? coreTokenForCode(raw.trim().toUpperCase()) : null);
    if (coreTok) return CONDITION_LABEL[coreTok] ?? raw;
    const extTok = extendedTokenFor(raw);
    const ext = extTok ? EXTENDED_ICD10[extTok] : undefined;
    return ext ? ext.display : raw;
  });
  const bundle = deriveIcdBundle({ conditions: canonicalEntries, subtrack_id: subtrackId });

  const perCondition: ResolvedCondition[] = entries.map((raw) => {
    const coreTok = normalizeCondition(raw)
      ?? (ICD10_CODE.test(raw.trim()) ? coreTokenForCode(raw.trim().toUpperCase()) : null);
    const core = coreTok ? CONDITION_ICD10[coreTok] : undefined;
    if (coreTok && core) {
      return {
        raw, label: CONDITION_LABEL[coreTok] ?? raw, icd10: core.code, display: core.display, extended: false, resolved: true,
      };
    }
    const extTok = extendedTokenFor(raw);
    const ext = extTok ? EXTENDED_ICD10[extTok] : undefined;
    if (ext) {
      return {
        raw, label: ext.display, icd10: ext.code, display: ext.display, extended: true, resolved: true,
      };
    }
    return {
      raw, label: raw, icd10: null, display: null, extended: false, resolved: false,
    };
  });

  const cmsView = bundle.cms_icd10.map((r) => ({ icd10: r.icd10, display: r.display, extended: !!r.extended }));
  // Wire projection ONLY — provenance keys stripped so stored == sent (R10).
  const payload: AlignmentConditionPayload[] = cmsView.map(({ icd10, display }) => ({ icd10, display }));

  return {
    perCondition,
    cmsView,
    payload,
    unresolvedCount: perCondition.filter((r) => !r.resolved).length,
    subtrackId,
  };
}
